import React, { useState, useEffect, useCallback } from 'react';
import { fixCountryName } from "../utility/formatCountry";
import { downloadCSV } from "../utility/downloadHelper";
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Helper function to generate clean export filenames for tables
const generateTableExportFilename = (title, property, year, category) => {
    if (!title) return 'TableExport';

    // Clean the title for filename use
    let filename = title
        .replace(/[^\w\s-]/g, '') // Remove special characters except spaces and hyphens
        .replace(/\s+/g, '_') // Replace spaces with underscores
        .replace(/_+/g, '_') // Replace multiple underscores with single
        .replace(/^_|_$/g, ''); // Remove leading/trailing underscores

    // Add category if specified
    if (category && !filename.toLowerCase().includes(category.toLowerCase())) {
        filename += `_${category}`;
    }

    // Add year if not already in title
    if (year && !filename.includes(year)) {
        filename += `_${year}`;
    }

    // Limit filename length
    if (filename.length > 100) {
        filename = filename.substring(0, 100);
    }

    return filename || 'TableExport';
};

// Helper function to generate summary text like in the web version
const generateSummaryText = (entry, category) => {
    const country = fixCountryName(entry.Country);
    const property = entry.property;
    const year = entry.year;
    const entryCategory = entry.category;

    if (property === 'powerTotal') {
        if (!entryCategory || entryCategory === '' || entryCategory === 'All') {
            return `Summary in ${country} of plants reported for WGC ${year}: `;
        } else {
            return `Summary in ${country} by ${entryCategory} technology of plants reported for WGC ${year}: `;
        }
    } else if (property === 'hcTotal') {
        if (!entryCategory || entryCategory === '' || entryCategory === 'All') {
            return `Summary in ${country}: `;
        } else {
            return `Summary for ${entryCategory} in ${country}: `;
        }
    }

    // Default behavior for other properties
    const catLabel = entryCategory ? ` for ${entryCategory}` : "";
    return `Summary${catLabel} in ${country}: `;
};


function formatNumber(val) {
    if (val === null || val === undefined || val === '') {
        return '';
    }

    // Convert the value to a string so we can check for '.'
    const strVal = val.toString();

    // If there's no decimal point, just return the original value as-is
    if (!strVal.includes('.')) {
        return val;
    }

    // Otherwise, parse and format to 2 decimals
    const parsed = parseFloat(strVal);
    if (isNaN(parsed)) {
        return val; // return original if parsing fails
    }

    return parsed.toFixed(2);
}

// Helper function to replace /yr with /year in field names
function fixFieldName(fieldName) {
    return fieldName ? fieldName.replace(/\/yr/g, '/year') : fieldName;
}

// 2) Standard aggregator fields for most properties.
//    For `hcProjects`, see buildHcProjectsAggregator below.
const aggregatorConfig = {
    powerData: [
        "Installed Capacity (MW)",
        "Gross Energy Produced (GEP) (GWh/year)"
    ],
    List: [
        "Installed Capacity (MW)",
        "Gross Energy Produced (GEP) (GWh/year)"
    ],
    hcData: [
        "Nominal Capacity (MWt)",
        "Energy used (net) (TJ/year)",
        "Energy produced (gross) (TJ/year)"
    ],
    power: [
        "Total Annual Gross Electrical Generation (GWh/year)",
        "Total Installed Capacity (MW)"
    ],
    hc: [
        "Total Annual Energy used (TJ/year)",
        "Total Nominal Capacity (MWt)"
    ],
    powerDataReport: [
        'Grand Total Installed Capacity (MWe)',
        'Total Gross Electrical Generation of reported plants'
    ],
    Report: [
        "Grand Total Gross Electrical generation GWh/year",
        "Grand Total Installed Capacity (MWe)",
        'Total Nominal Capacity of reported plants',
        'Total Gross Electrical Generation of reported plants',
        'Total number of reported plants'
    ],
    // For hcProjects => partial aggregator logic
    hcProjects: true,
    powerTotal: [
        "Gross Electrical Generation (GWh/yr)",
        "Nominal Capacity (MWt)"
    ],
    hcTotal: [
        "Total Energy used (TJ/year)",
        "Total Nominal Capacity (MWt)"
    ],
    powerOperation: [
        'Installed Capacity (MW)',
        'Gross Energy Produced (GEP) (GWh/year)'
    ],
    // The following do not use aggregator
    countries: null,
};

// 3) Category field per property
function getCategoryField(property) {
    switch (property) {
        case "power":
        case "hc":
            return "Typology";
        case "hcProjects":
            return "Geothermal Application";
        case "powerTotal":
            return "Technology";
        case "hcTotal":
            return "Application";
        case "powerOperation":
            return "Technology";
        default:
            return null;
    }
}

/**
 * Main aggregator function.
 * If property=hcProjects => see buildHcProjectsAggregator.
 */
function buildAggregatedRows(rows, property, firstSelectedCountry, category, year) {
    if (!rows || rows.length === 0) {
        return [];
    }

    // 1) If property is countries => skip aggregator
    if (property === "countries") {
        return rows;
    }

    // 2) If aggregatorConfig[property] is "true", that means "hcProjects"
    const fields = aggregatorConfig[property];
    if (!fields && fields !== true) {
        // no aggregator
        return rows;
    }

    // 3) if property === hcProjects => see buildHcProjectsAggregator
    if (property === "hcProjects") {
        return buildHcProjectsAggregator(rows, firstSelectedCountry);
    }

    // 4) Normal aggregator logic for standard props
    const geaCodes = ["World", "OECD", "APAC", "EMEA", "LATAM", "NA",
        "Africa", "Americas", "Asia", "Europe", "Oceania"];
    const hasGlobalCode = geaCodes.includes(firstSelectedCountry);

    // For powerDataReport, we need to handle both existing summaries and normal rows
    let rowsToProcess = rows;

    // group rows by country (exclude _no_country_ rows)
    const grouped = new Map();
    rowsToProcess.forEach(r => {
        const c = r.Country;
        if (!c || c === "_no_country_") return; // Skip rows without proper country
        if (!grouped.has(c)) grouped.set(c, []);
        grouped.get(c).push(r);
    });

    // track order (exclude _no_country_)
    const countryOrder = [];
    rowsToProcess.forEach(r => {
        const c = r.Country;
        if (c && c !== "_no_country_" && !countryOrder.includes(c)) {
            countryOrder.push(c);
        }
    });

    const final = [];
    const globalSum = {};
    fields.forEach(f => { globalSum[f] = 0; });

    if (property === "powerDataReport") {
        // For powerDataReport, we need to handle existing summaries properly
        const existingCountrySummaries = rows.filter(r => r.type === 'summary');
        const normalRows = rows.filter(r => !r.type || r.type === 'normal');

        console.log('=== POWERDATAREPORT DEBUG ===');
        console.log('Total rows received:', rows.length);
        console.log('Existing country summaries:', existingCountrySummaries.length);
        console.log('Normal rows:', normalRows.length);
        console.log('First selected country:', firstSelectedCountry);

        if (existingCountrySummaries.length > 0) {
            // Use existing country summaries - keep them as they are
            console.log('SCENARIO: Using existing country summaries (Manual selection)');
            console.log('powerDataReport: Found existing country summaries:', existingCountrySummaries.length);

            // Add all rows (normal + existing summaries) in their original order
            final.push(...rows);

            // Sum the existing country summary rows for final regional summary
            existingCountrySummaries.forEach(r => {
                console.log('Country summary row:', r);
                fields.forEach(f => {
                    if (r[f] !== undefined && r[f] !== null) {
                        const value = parseFloat(r[f]) || 0;
                        globalSum[f] += value;
                        console.log(`Field ${f}: ${r[f]} -> ${value}, total: ${globalSum[f]}`);
                    }
                });
            });
        } else {
            // Fall back to standard aggregation if no existing summaries
            // This handles the case when switching from List to Report
            console.log('SCENARIO: Creating new summaries from normal rows (List to Report)');
            // We need to group normal rows by country for aggregation
            const normalGrouped = new Map();
            normalRows.forEach(r => {
                const c = r.Country;
                if (!c || c === "_no_country_") return;
                if (!normalGrouped.has(c)) normalGrouped.set(c, []);
                normalGrouped.get(c).push(r);
            });

            // Get country order from normal rows
            const normalCountryOrder = [];
            normalRows.forEach(r => {
                const c = r.Country;
                if (c && c !== "_no_country_" && !normalCountryOrder.includes(c)) {
                    normalCountryOrder.push(c);
                }
            });

            normalCountryOrder.forEach(c => {
                const rowList = normalGrouped.get(c) || [];
                if (!rowList.length) return;

                // Push all normal rows
                rowList.forEach(rr => final.push(rr));

                // Calculate summary for processed rows
                const sumRow = {
                    type: 'summary',
                    Country: c,
                    property: property,
                    category: category,
                    year: year
                };
                fields.forEach(f => {
                    let sumVal = 0;
                    rowList.forEach(rr => {
                        sumVal += parseFloat(rr[f]) || 0;
                    });
                    sumRow[f] = sumVal;
                    if (hasGlobalCode) {
                        globalSum[f] += sumVal;
                    }
                });
                final.push(sumRow);
            });
        }

        // Skip the standard aggregation logic for powerDataReport
        // We'll add the final regional summary after this if-else block
    } else {
        // Standard aggregation logic for other properties
        countryOrder.forEach(c => {
            const rowList = grouped.get(c) || [];
            if (!rowList.length) return;

            // Filter out existing summary rows to avoid duplicates
            const normalRows = rowList.filter(rr => !rr.type || rr.type === 'normal');

            // For hcTotal with category filter, only include rows that match the category
            let rowsToProcess = normalRows;
            if (property === 'hcTotal' && category) {
                const categoryField = getCategoryField(property);
                rowsToProcess = normalRows.filter(rr => rr[categoryField] === category);
                if (rowsToProcess.length === 0) return; // Skip this country if no matching data
            }

            // Push all normal rows
            rowsToProcess.forEach(rr => final.push(rr));

            // Calculate summary for processed rows
            const sumRow = {
                type: 'summary',
                Country: c,
                property: property,
                category: category,
                year: year
            };
            fields.forEach(f => {
                let sumVal = 0;
                rowsToProcess.forEach(rr => {
                    sumVal += parseFloat(rr[f]) || 0;
                });
                sumRow[f] = sumVal;
                if (hasGlobalCode) {
                    globalSum[f] += sumVal;
                }
            });
            final.push(sumRow);
        });
    }

    // If "World"/GEA => final aggregator row
    if (hasGlobalCode && Object.values(globalSum).some(val => val > 0)) {
        console.log('=== CREATING FINAL REGIONAL SUMMARY ===');
        console.log('Global sum values:', globalSum);
        console.log('Has global code:', hasGlobalCode);

        const globalRow = {
            type: 'countrySummary',
            Country: firstSelectedCountry,
            property: property,
            category: category,
            year: year
        };

        if (property === "powerDataReport") {
            // Map the aggregated fields to the correct display names for final regional summary
            globalRow['Total Installed Capacity (MW)'] = globalSum['Grand Total Installed Capacity (MWe)'];
            globalRow['Total Gross Electrical generation (GWh/year)'] = globalSum['Total Gross Electrical Generation of reported plants'];

            console.log('Final regional summary for powerDataReport:');
            console.log('- Total Installed Capacity (MW):', globalRow['Total Installed Capacity (MW)']);
            console.log('- Total Gross Electrical generation (GWh/year):', globalRow['Total Gross Electrical generation (GWh/year)']);
        } else if (property === "powerOperation") {
            // Map the aggregated fields to the correct display names for final regional summary
            globalRow['Total Installed Capacity (MWe)'] = globalSum['Installed Capacity (MW)'];
            globalRow['Total Gross Electrical generation GWh/year'] = globalSum['Gross Energy Produced (GEP) (GWh/year)'];
        } else {
            // Standard mapping for other properties
            fields.forEach(f => {
                globalRow[f] = globalSum[f];
            });
        }

        final.push(globalRow);
    }

    return final;
}

/**
 * 4b) Special aggregator for "hcProjects" when user picks a category.
 * Summation logic for large projects only:
 *  "Total Nominal Capacity of large projects (MW)"
 *  "Total Energy Used by large projects (TJ/year)"
 *  "Total number of large projects"
 * Everything else remains the same. 
 * If category === "", we skip this aggregator (see above).
 */
function buildHcProjectsAggregator(rows, firstSelectedCountry) {
    const geaCodes = ["World", "OECD", "APAC", "EMEA", "LATAM", "NA",
        "Africa", "Americas", "Asia", "Europe", "Oceania"];
    const hasGlobalCode = geaCodes.includes(firstSelectedCountry);

    // group rows by country (exclude _no_country_ rows)
    const grouped = new Map();
    rows.forEach(r => {
        const c = r.Country;
        if (!c || c === "_no_country_") return; // Skip rows without proper country
        if (!grouped.has(c)) grouped.set(c, []);
        grouped.get(c).push(r);
    });

    // track order (exclude _no_country_)
    const countryOrder = [];
    rows.forEach(r => {
        const c = r.Country;
        if (c && c !== "_no_country_" && !countryOrder.includes(c)) {
            countryOrder.push(c);
        }
    });

    const final = [];

    let globalLargeCap = 0;
    let globalLargeUsed = 0;
    let globalProjectCount = 0;

    countryOrder.forEach(c => {
        const rowList = grouped.get(c) || [];
        if (!rowList.length) return;
        // push normal rows
        rowList.forEach(rr => {
            // ignore any old summary from the backend
            if (rr.type !== 'summary') {
                final.push(rr);
            }
        });

        // aggregator row
        let sumLargeCap = 0;
        let sumLargeUsed = 0;
        let sumProjCount = 0;

        rowList.forEach(rr => {
            sumLargeCap += parseFloat(rr["Nominal Capacity (MW)"]) || 0;
            sumLargeUsed += parseFloat(rr["Energy Used (TJ/yr)"]) || 0;
            sumProjCount++;
        });

        // produce new summary
        const sumRow = {
            type: 'summary',
            Country: c,
            "Total Nominal Capacity of large projects (MW)": sumLargeCap,
            "Total Energy Used by large projects (TJ/year)": sumLargeUsed,
            "Total number of large projects": sumProjCount,
        };
        final.push(sumRow);

        // global aggregator
        if (hasGlobalCode) {
            globalLargeCap += sumLargeCap;
            globalLargeUsed += sumLargeUsed;
            globalProjectCount += sumProjCount;
        }
    });

    // final aggregator row if gea
    if (hasGlobalCode) {
        final.push({
            type: 'countrySummary',
            Country: firstSelectedCountry,
            "Total Nominal Capacity of large projects (MW)": globalLargeCap,
            "Total Energy Used by large projects (TJ/year)": globalLargeUsed,
            "Total number of large projects": globalProjectCount,
        });
    }

    return final;
}

// 5) Custom render for "countries"
function renderCountriesRows(data) {
    return data.map((row, i) => (
        <tr key={i} className="normal-row">
            <td>{fixCountryName(row.Country)}</td>
            <td>{row['Total Gross Electrical generation GWh/year'] || ''}</td>
            <td>{row['Total Electrical Installed Capacity (MWe)'] || ''}</td>
            <td>{row['Total Energy used (TJ/year)'] || ''}</td>
            <td>{row['Total H&C Installed Capacity (MWt)'] || ''}</td>
        </tr>
    ));
}

// 6) Custom render for "powerOperation"
function renderPowerOperationRows(data, validColumns) {
    // Summaries come from the backend (reservoirTypeSummary, etc.).
    return data.map((entry, index) => {
        if (entry.type === 'normal') {
            return (
                <tr key={`entry-${index}`} className="normal-row">
                    {validColumns.map((col, i) => {
                        const val = entry[col];
                        if (col === 'Country') {
                            return <td key={i}>{fixCountryName(entry[col])}</td>;
                        }
                        return <td key={i}>{formatNumber(val)}</td>;
                    })}
                </tr>
            );
        }
        else if (entry.type === 'reservoirTypeSummary') {
            const s = [
                `${entry.Technology}:`,
                `Total Installed Capacity (MW): ${formatNumber(entry['Total Installed Capacity (MW)'])}`,
                `Gross Energy Produced (GEP) (GWh/year): ${formatNumber(entry['Gross Energy Produced (GEP) (GWh/year)'])}`,
                `Total number of units: ${entry['Total number of units']}`
            ];
            return (
                <tr key={`summary-type-${index}`}>
                    <td colSpan={validColumns.length}>
                        <div className="last-entry-row">{s.join(' — ')}</div>
                    </td>
                </tr>
            );
        }
        else if (entry.type === 'geothermalFieldSummary') {
            const fieldName = entry['Geothermal Field'] || 'Unnamed Geothermal Field';
            const s = [
                `${fieldName}:`,
                `Total Installed Capacity (MW): ${formatNumber(entry['Total Installed Capacity (MW)'])}`,
                `Gross Energy Produced (GEP) (GWh/year): ${formatNumber(entry['Gross Energy Produced (GEP) (GWh/year)'])}`,
                `Total number of units: ${entry['Total number of units']}`
            ];
            return (
                <tr key={`summary-field-${index}`}>
                    <td colSpan={validColumns.length}>
                        <div className="last-entry-field">{s.join(' — ')}</div>
                    </td>
                </tr>
            );
        }
        else if (entry.type === 'countrySummary') {
            const s = [
                `${fixCountryName(entry.Country)} reported operating plants:`,
                `Total Gross Electrical generation GWh/year: ${formatNumber(entry['Total Gross Electrical generation GWh/yr'])}`,
                `Total Installed Capacity (MWe): ${formatNumber(entry['Total Installed Capacity (MWe)'])}`,
                `Total number of units: ${entry['Total number of units']}`,
            ];
            return (
                <tr key={`summary-country-${index}`}>
                    <td colSpan={validColumns.length}>
                        <div className="last-entry-country">{s.join(' — ')}</div>
                    </td>
                </tr>
            );
        }
        return null;
    });
}

// Function to generate custom summary labels
function generateSummaryLabel(entry, category) {
    const country = fixCountryName(entry.Country);
    const property = entry.property;
    const year = entry.year;
    const entryCategory = entry.category;

    if (property === 'powerTotal') {
        if (!entryCategory || entryCategory === '' || entryCategory === 'All') {
            return `Summary in ${country} of plants reported for WGC ${year}: `;
        } else {
            return `Summary in ${country} by ${entryCategory} technology of plants reported for WGC ${year}: `;
        }
    } else if (property === 'powerOperation') {
        if (!entryCategory || entryCategory === '' || entryCategory === 'All') {
            return `${country} reported operating plants: `;
        } else {
            return `Summary in ${country} by ${entryCategory} technology: `;
        }
    } else if (property === 'hcTotal') {
        if (!entryCategory || entryCategory === '' || entryCategory === 'All') {
            return `Summary in ${country}: `;
        } else {
            return `Summary for ${entryCategory} in ${country}: `;
        }
    }

    // Default behavior for other properties
    const catLabel = entryCategory ? ` for ${entryCategory}` : "";
    return `Summary${catLabel} in ${country}: `;
}

function generateCountrySummaryLabel(entry, category) {
    const country = fixCountryName(entry.Country);
    const property = entry.property;
    const entryCategory = entry.category;

    if (property === 'hcTotal') {
        if (!entryCategory || entryCategory === '' || entryCategory === 'All') {
            return `${country}: `;
        } else {
            return `Summary ${country} for ${entryCategory}: `;
        }
    }

    // Default behavior
    const catLabel = entryCategory ? ` for ${entryCategory}` : "";
    return `${country}${catLabel}: `;
}

// 7) Standard row rendering (for aggregator or normal summaries)
function renderStandardRows(data, validColumns, category) {
    return data.map((entry, i) => {
        if (entry.type === 'summary') {
            // Summation row for that country
            const lines = [];
            Object.keys(entry).forEach(k => {
                if (k !== 'type' && k !== 'Country' && k !== 'property' && k !== 'category' && k !== 'year') {
                    // Fix /yr to /year in field names
                    const fixedFieldName = fixFieldName(k);
                    lines.push(`${fixedFieldName}: ${formatNumber(entry[k])}`);
                }
            });

            const label = generateSummaryLabel(entry, category);

            return (
                <tr key={`summary-${i}`}>
                    <td colSpan={validColumns.length}>
                        <div className="last-entry-row">
                            {label + lines.join(" — ")}
                        </div>
                    </td>
                </tr>
            );
        }
        else if (entry.type === 'countrySummary') {
            // World/GEA aggregator row
            const lines = [];
            Object.keys(entry).forEach(k => {
                if (k !== 'type' && k !== 'Country' && k !== 'property' && k !== 'category' && k !== 'year') {
                    // Fix /yr to /year in field names
                    const fixedFieldName = fixFieldName(k);
                    lines.push(`${fixedFieldName}: ${formatNumber(entry[k])}`);
                }
            });

            const label = generateCountrySummaryLabel(entry, category);

            return (
                <tr key={`csummary-${i}`}>
                    <td colSpan={validColumns.length}>
                        <div className="last-entry-country">
                            {entry.Country ? label + lines.join(" — ") : lines.join(" — ")}
                        </div>
                    </td>
                </tr>
            );
        }
        else {
            // Normal row
            return (
                <tr key={`row-${i}`} className="normal-row">
                    {validColumns.map((col, idx) => {
                        if (col === 'Country') {
                            return <td key={idx}>{fixCountryName(entry[col])}</td>;
                        }
                        return <td key={idx}>{formatNumber(entry[col])}</td>;
                    })}
                </tr>
            );
        }
    });
}

// 8) Decide which columns to show (normal rows only)
function getValidColumns(data) {
    const allKeys = new Set();
    data.forEach(r => {
        if (!r.type || r.type === 'normal') {
            Object.keys(r).forEach(k => {
                if (k !== 'type') allKeys.add(k);
            });
        }
    });
    return Array.from(allKeys);
}

// 9) Main Table
export const Table = ({
    data,
    property,
    year,
    triggerExportPDF,
    triggerExportCSV,
    country
}) => {
    const [tableData, setTableData] = useState([]);
    const [start, setStart] = useState(false);
    const [validColumns, setValidColumns] = useState([]);

    // For the "category" filtering
    const [category, setCategory] = useState("");
    const categoryField = getCategoryField(property);

    // We'll check if first selected is "World"/"OECD"/GEA:
    const firstSelected = country?.[0]?.value;

    // On data change
    useEffect(() => {
        setStart(false);
        if (Array.isArray(data)) {
            // If data can be nested, do an extra `.flat()`
            const rawRows = data.flat().filter(Boolean);
            let normalRows;

            const shouldKeepBackendSummaries = property === "powerDataReport" || property === "Report" || property === "hcTotal";

            normalRows = rawRows.filter(row => {
                if (shouldKeepBackendSummaries) return true;
                return row.type !== 'summary' && row.type !== 'countrySummary';
            });

            console.log('=== TABLE COMPONENT DATA CHANGE ===');
            console.log('Property:', property);
            console.log('Raw rows count:', rawRows.length);
            console.log('Normal rows count:', normalRows.length);
            console.log('Should keep backend summaries:', shouldKeepBackendSummaries);
            console.log('First selected country:', firstSelected);

            const cols = getValidColumns(normalRows);
            setValidColumns(cols);
            setTableData(normalRows);
            setCategory(""); // reset filter
            setStart(true);
        }
    }, [data, property]);

    // 9a) Filter by category if user selected one
    let filteredRows = tableData;
    if (categoryField && category) {
        filteredRows = tableData.filter(r => {
            // Only include normal rows that match the category
            if (!r.type || r.type === 'normal') {
                return r[categoryField] === category;
            }
            // Skip all summary rows during filtering - they'll be regenerated
            return false;
        });
    }

    // 9b) Build aggregator rows for standard properties
    let finalRows;
    if (property === "countries" || property === "Report") {
        // These properties don't use aggregation
        finalRows = filteredRows;
    } else if (property === "powerDataReport") {
        // For powerDataReport, we keep backend summaries but need to add final regional summary
        finalRows = buildAggregatedRows(filteredRows, property, firstSelected, category, year);
    } else {
        // Standard aggregation for other properties including powerOperation
        finalRows = buildAggregatedRows(filteredRows, property, firstSelected, category, year);
    }

    // Ensure summaries are included for export (always include all rows for export)
    const actualFinalRows = finalRows;

    // 9c) Render the final data
    let renderedRows;
    if (property === "countries") {
        renderedRows = renderCountriesRows(actualFinalRows);
    } else {
        renderedRows = renderStandardRows(actualFinalRows, validColumns, category);
    }

    // CSV export
    const handleExportCSV = useCallback(() => {
        // Check browser compatibility
        if (!window.Blob || !window.URL) {
            alert('CSV export is not supported in this browser. Please use a modern browser like Chrome, Firefox, or Safari.');
            return;
        }

        if (!actualFinalRows.length) {
            alert('No data available to export.');
            return;
        }

        try {
            // Separate normal rows from summary rows
            const normalRows = actualFinalRows.filter(row => !row.type || row.type === 'normal');
            const summaryRows = actualFinalRows.filter(row => row.type === 'summary' || row.type === 'countrySummary');

            // Collect columns from normal rows only
            const colSet = new Set();
            normalRows.forEach(r => {
                Object.keys(r).forEach(k => {
                    if (k !== 'type' && k !== 'property' && k !== 'category' && k !== 'year') colSet.add(k);
                });
            });
            const cols = Array.from(colSet);

            // Build CSV content - start directly with table headers
            const csvRows = [];

            // Add header row (this is where the actual table starts)
            csvRows.push(cols.map(col => `"${col}"`).join(','));

            // Add mixed data and summary rows in correct order (like web display and PDF)
            actualFinalRows.forEach(row => {
                if (!row.type || row.type === 'normal') {
                    // Normal data row
                    const dataRow = cols.map(c => {
                        if (c === 'Country') {
                            return `"${fixCountryName(row[c])}"`;
                        } else {
                            return `"${formatNumber(row[c]) || ''}"`;
                        }
                    });
                    csvRows.push(dataRow.join(','));
                } else if (row.type === 'summary' || row.type === 'countrySummary') {
                    // Summary row - break text at logical points for better CSV display
                    const summaryText = generateSummaryText(row, category);
                    const summaryData = [];
                    Object.keys(row).forEach(k => {
                        if (k !== 'type' && k !== 'property' && k !== 'category' && k !== 'year' && k !== 'Country') {
                            const fixedFieldName = k.replace(/\/yr/g, '/year');
                            summaryData.push(`${fixedFieldName}: ${formatNumber(row[k])}`);
                        }
                    });

                    // Create the full summary text
                    const fullSummaryText = `${summaryText}${summaryData.join(' — ')}`;

                    // Break at logical points: first after the summary label, then at each "—"
                    const lines = [];

                    // First line: just the summary label (e.g., "Summary in Australia:")
                    lines.push(summaryText.trim());

                    // Remaining lines: split by "—" separator
                    summaryData.forEach(dataPoint => {
                        lines.push(`  ${dataPoint}`); // Indent data points
                    });

                    // Add each line as a separate row
                    lines.forEach((line, index) => {
                        const summaryRow = [`"${line}"`];
                        for (let i = 1; i < cols.length; i++) {
                            summaryRow.push('""'); // Empty columns
                        }
                        csvRows.push(summaryRow.join(','));
                    });
                }
            });

            // Create CSV content
            const csvContent = csvRows.join('\n');

            // Generate filename using the same logic as PDF export
            const title = titleFinderForPDF(property, countryFinder(country), year, category);
            const filename = generateTableExportFilename(title, property, year, category);

            // Create and download the file
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.setAttribute('href', url);
            a.setAttribute('download', `${filename}.csv`);
            a.style.display = 'none';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('CSV export error:', error);
            alert('CSV export failed. Please try again or use a different browser.');
        }
    }, [actualFinalRows, property, country, year, category]);

    const handleExportPDF = useCallback(() => {
        // Check browser compatibility
        if (!window.Blob || !window.URL) {
            alert('PDF export is not supported in this browser. Please use a modern browser like Chrome, Firefox, or Safari.');
            return;
        }

        if (!actualFinalRows.length || !validColumns.length) {
            alert('No data available to export.');
            return;
        }

        try {
            const doc = new jsPDF('l', 'mm', 'a3');

            // Build mixed table body with summaries in correct positions
            const body = [];
            let currentCountry = null;

            actualFinalRows.forEach((row, index) => {
                if (!row.type || row.type === 'normal') {
                    // Normal data row
                    const dataRow = validColumns.map(col => {
                        if (col === 'Country') {
                            return fixCountryName(row[col]);
                        } else {
                            return formatNumber(row[col]);
                        }
                    });
                    body.push(dataRow);
                    currentCountry = row.Country;
                } else if (row.type === 'summary' || row.type === 'countrySummary') {
                    // Summary row - create a special row that spans all columns
                    const summaryText = generateSummaryText(row, category);
                    const summaryData = [];
                    Object.keys(row).forEach(k => {
                        if (k !== 'type' && k !== 'property' && k !== 'category' && k !== 'year' && k !== 'Country') {
                            const fixedFieldName = k.replace(/\/yr/g, '/year');
                            summaryData.push(`${fixedFieldName}: ${formatNumber(row[k])}`);
                        }
                    });
                    const fullSummaryText = `${summaryText}${summaryData.join(' — ')}`;

                    // Create a row with the summary text in the first column and empty others
                    const summaryRow = [fullSummaryText];
                    for (let i = 1; i < validColumns.length; i++) {
                        summaryRow.push('');
                    }
                    body.push(summaryRow);
                }
            });

            // Create the table
            autoTable(doc, {
                head: [validColumns],
                body: body,
                startY: 40,
                styles: {
                    fontSize: 6,
                    overflow: 'linebreak',
                    cellPadding: 2,
                    halign: 'center',
                },
                headStyles: {
                    fillColor: [255, 102, 0], // orange header
                    textColor: 255,
                    fontSize: 7,
                    halign: 'center',
                },
                columnStyles: {
                    // Make country column wider
                    0: { cellWidth: 25 },
                },
                margin: { top: 40 },
                didDrawPage: (data) => {
                    // Add title with better formatting
                    const title = titleFinderForPDF(property, countryFinder(country), year, category);

                    // Set title font - make it orange
                    doc.setFontSize(12);
                    doc.setFont(undefined, 'bold');
                    doc.setTextColor(255, 102, 0); // Orange color

                    // Split long titles into multiple lines
                    const maxWidth = doc.internal.pageSize.width - 20;
                    const titleLines = doc.splitTextToSize(title, maxWidth);

                    // Draw title lines
                    let yPosition = 15;
                    titleLines.forEach((line, index) => {
                        doc.text(line, data.settings.margin.left, yPosition + (index * 5));
                    });

                    // Add subtitle if available
                    const subtitle = subtitleFinder(property);
                    if (subtitle) {
                        doc.setFontSize(8);
                        doc.setFont(undefined, 'normal');
                        doc.setTextColor(0, 0, 0); // Reset to black
                        const subtitleLines = doc.splitTextToSize(subtitle, maxWidth);
                        yPosition += (titleLines.length * 5) + 5;
                        subtitleLines.forEach((line, index) => {
                            doc.text(line, data.settings.margin.left, yPosition + (index * 4));
                        });
                    }
                },
                didParseCell: function (data) {
                    // Style summary rows differently
                    if (data.row.index < body.length) {
                        const rowData = body[data.row.index];
                        if (rowData && rowData[0] && rowData[0].includes('Summary in')) {
                            // This is a summary row
                            data.cell.styles.fillColor = [255, 245, 230]; // Light orange background
                            data.cell.styles.textColor = [0, 0, 0];
                            data.cell.styles.fontStyle = 'normal';
                            data.cell.styles.fontSize = 7;

                            // Make summary row span all columns
                            if (data.column.index === 0) {
                                data.cell.colSpan = validColumns.length;
                            } else {
                                data.cell.text = '';
                            }
                        }
                    }
                }
            });

            // Generate filename
            const title = titleFinder(property, countryFinder(country), year, category);
            const filename = generateTableExportFilename(title, property, year, category);

            // Save the file
            doc.save(`${filename}.pdf`);
        } catch (error) {
            console.error('Table PDF export error:', error);
            alert('Table PDF export failed. Please try again or use a different browser.');
        }
    }, [actualFinalRows, validColumns, property, country, year, category]);

    useEffect(() => {
        if (start && triggerExportCSV) {
            triggerExportCSV(handleExportCSV);
        }
    }, [start, triggerExportCSV]);

    useEffect(() => {
        if (start && triggerExportPDF) {
            triggerExportPDF(handleExportPDF);
        }
    }, [start, triggerExportPDF]);

    if (!start) return <div></div>;

    // Build category dropdown
    let categoryOptions = [];
    if (categoryField) {
        const distinct = new Set();
        tableData.forEach(r => {
            if (!r.type || r.type === 'normal') {
                if (r[categoryField]) {
                    distinct.add(r[categoryField]);
                }
            }
        });
        categoryOptions = Array.from(distinct);
    }

    return (
        <div id="exportTableDiv" className={property === "countries" ? "countries-list" : ""}>
            <div id='fixedTitleTable'>
                <h1 className='titles' id={!categoryField ? "noCategoriesTitle" : ''}>{titleFinder(property, country, year, category)}</h1>
                <h2 className='subtitle' id={!categoryField ? "noCategoriesTitle" : ''}>{subtitleFinder(property)}</h2>

                {/* If property has a category field, let user pick which category to display */}
                {categoryField && (
                    <select
                        className="input"
                        id="dataCategoriesBar"
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                    >
                        <option value="">All</option>
                        {categoryOptions.map((c, idx) => (
                            <option key={idx} value={c}>
                                {c}
                            </option>
                        ))}
                    </select>
                )}
            </div>

            <div className="table-container">
                {renderedRows && renderedRows.length > 0 ? (
                    <table>
                        <thead className="orange">
                            {property === "countries" ? (
                                <tr>
                                    <th>Country</th>
                                    <th>Total Gross Electrical generation (GWh/year)</th>
                                    <th>Total Electrical Installed Capacity (MWe)</th>
                                    <th>Total H&C Energy used (TJ/year)</th>
                                    <th>Total H&C Installed Capacity (MWt)</th>
                                </tr>
                            ) : property === "powerOperation" ? (
                                <tr>
                                    {validColumns.map((col, i) => (
                                        <th key={i}>{fixFieldName(col)}</th>
                                    ))}
                                </tr>
                            ) : (
                                <tr>
                                    {validColumns.map((col, i) => (
                                        <th key={i}>{fixFieldName(col)}</th>
                                    ))}
                                </tr>
                            )}
                        </thead>
                        <tbody>{renderedRows}</tbody>
                    </table>
                ) : (
                    <p className="error">No data available for the specified filter.</p>
                )}
            </div>
        </div>
    );
};


function titleFinder(activeProperty, countryTitle, year, category) {
    const catLabel = category ? ` for ${category} Technology` : "";

    const countryNames = Array.isArray(countryTitle)
        ? countryTitle.map((country, index) =>
            fixCountryName(country?.value || '') + (index === countryTitle.length - 1 ? ' ' : ', ')
        ).join('')
        : fixCountryName(countryTitle?.value || '');

    switch (activeProperty) {
        case 'powerData':
        case 'List':
            return `List of Geothermal power plants${catLabel} reported in ${countryNames}for WGC ${year}`;
        case 'hcData':
            return `List of Geothermal Heating and Cooling large projects${catLabel} reported in ${countryNames}for WGC ${year}`;
        case 'countries':
            return `Countries with Geothermal Power Generation/Geothermal Heating and Cooling ${catLabel} in ${countryNames}for WGC ${year}`;
        case 'power':
            return `Geothermal Total Power Installed Capacity and Production${catLabel} in ${countryNames}for WGC ${year}`;
        case 'hc':
            return `Geothermal Heating&Cooling Total Installed Capacity and Production${catLabel} in ${countryNames}for WGC ${year}`;
        case 'powerDataReport':
        case 'Report':
            return `Geothermal power plants${catLabel} in ${countryNames}reported for WGC ${year}`;
        case 'powerOperation':
            return `Geothermal power plants in operation by technology${catLabel} as reported for WGC ${year}`;
        case 'hcProjects':
            return `Geothermal H&C Projects${catLabel} in ${countryNames}as reported for WGC ${year}`;
        case 'powerTotal':
            return `Geothermal Power${catLabel} in ${countryNames}from plants as reported for WGC ${year}`;
        case 'hcTotal':
            return `Geothermal H&C${catLabel} in ${countryNames}reported for WGC ${year}`;
        default:
            return "Table Export";
    }
}

// Separate function for PDF export that preserves original formatting
function titleFinderForPDF(activeProperty, countryTitle, year, category) {
    const catLabel = category ? ` for ${category} Technology` : "";

    const countryNames = Array.isArray(countryTitle)
        ? countryTitle.map((country, index) =>
            (country?.value || '') + (index === countryTitle.length - 1 ? ' ' : ', ')
        ).join('')
        : (countryTitle?.value || '');

    switch (activeProperty) {
        case 'powerData':
        case 'List':
            return `List of Geothermal power plants${catLabel} reported in ${countryNames}for WGC ${year}`;
        case 'hcData':
            return `List of Geothermal Heating and Cooling large projects${catLabel} reported in ${countryNames}for WGC ${year}`;
        case 'countries':
            return `Countries with Geothermal Power Generation/Geothermal Heating and Cooling ${catLabel} in ${countryNames}for WGC ${year}`;
        case 'power':
            return `Geothermal Total Power Installed Capacity and Production${catLabel} in ${countryNames}for WGC ${year}`;
        case 'hc':
            return `Geothermal Heating&Cooling Total Installed Capacity and Production${catLabel} in ${countryNames}for WGC ${year}`;
        case 'powerDataReport':
        case 'Report':
            return `Geothermal power plants${catLabel} in ${countryNames}reported for WGC ${year}`;
        case 'powerOperation':
            return `Geothermal power plants in operation by technology${catLabel} as reported for WGC ${year}`;
        case 'hcProjects':
            return `Geothermal H&C Projects${catLabel} in ${countryNames}as reported for WGC ${year}`;
        case 'powerTotal':
            return `Geothermal Power${catLabel} in ${countryNames}from plants as reported for WGC ${year}`;
        case 'hcTotal':
            return `Geothermal H&C${catLabel} in ${countryNames}reported for WGC ${year}`;
        default:
            return "Table Export";
    }
}

function subtitleFinder(activeProperty) {
    switch (activeProperty) {
        case 'powerData':
        case 'List':
            return "Please note that the following values refer to the projects and plants described in detail in the WGC reporting. They may overlook project data and fail to achieve the actual Grand Total values";
        case 'hcData':
            return "Please note that the following values refer to the projects and plants described in detail in the WGC reporting. They may overlook project data and fail to achieve the actual Grand Total values";
        case 'power':
            return "Please note that the following values refer to the projects and plants described in detail in the WGC reporting. They may overlook project data and fail to achieve the actual Grand Total values";
        case 'hc':
            return "Please note that the following values refer to the projects and plants described in detail in the WGC reporting. They may overlook project data and fail to achieve the actual Grand Total values";
        case 'powerDataReport':
        case 'Report':
        case 'powerOperation':
            return "Please note that the following values refer to the projects and plants described in detail in the WGC reporting. They may overlook project data and fail to achieve the actual Grand Total values";
        case 'hcProjects':
            return "Please note that the following values refer to the projects and plants described in detail in the WGC reporting. They may overlook project data and fail to achieve the actual Grand Total values";
        case 'powerTotal':
        case 'hcTotal':
            return "Please note that the following values refer to the projects and plants described in detail in the WGC reporting. They may overlook project data and fail to achieve the actual Grand Total values";
        default:
            return "";
    }
}

function countryFinder(oldCountry) {
    if (!oldCountry || !Array.isArray(oldCountry)) return [];

    const renamedCountries = oldCountry.map(countryName => {
        const value = countryName?.value;
        if (!value) return null;

        switch (value) {
            case "World":
                return { value: "World" };
            case "OECD":
                return { value: "OECD - Organization For Economic Co-operation and Development" };
            case "APAC":
                return { value: "APAC - Asia Pacific" };
            case "EMEA":
                return { value: "EMEA - Europe, Middle East and Africa" };
            case "LATAM":
                return { value: "LATAM - Latin America" };
            case "NA":
                return { value: "NA - North America" };
            default:
                return { value }; // keep original
        }
    }).filter(Boolean);

    return renamedCountries;
}